import React, { useEffect, useRef } from 'react';
import { LuChevronDown, LuChevronUp, LuInfo, LuPencil } from 'react-icons/lu';
import { translate } from '../../../../utils/Common';

const t = (key) => translate(`dashboard.platforms.doors.position.${key}`);

/**
 * Ubica una puerta guardada dentro de la columna que se le eligió, sin modal: se despliega bajo el
 * select de columna con las puertas de esa columna y la que se mueve resaltada. Se coloca tocando
 * un hueco («Mover aquí») o con las flechas (botones o teclado), y el Preview la sigue en vivo.
 * `position` es su índice entre las demás puertas de la columna (`doors`, sin ella).
 */
const DoorPositionPicker = ({ columnId, doors, movedDoor, position, onChange, affectedCount, collapsed, onCollapse, onExpand }) => {
  const movedRef = useRef(null);
  const total = doors.length + 1;

  useEffect(() => {
    if (!collapsed) movedRef.current?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  }, [position, collapsed]);

  const move = (nextPosition) => {
    if (nextPosition < 0 || nextPosition > doors.length || nextPosition === position) return;
    onChange(nextPosition);
  };

  const affectedText = affectedCount > 0 && (
    <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
      <LuInfo size={13} className="shrink-0" />
      {affectedCount} {affectedCount === 1 ? t('affected.one') : t('affected.other')}
    </p>
  );

  if (collapsed) {
    return (
      <div className="col-span-12 mb-3 flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/50 px-3 py-2 animate-in fade-in-0 duration-200">
        <div className="min-w-0">
          <p className="text-sm text-foreground">
            {t('column')} {columnId} · {t('position')} <span className="font-semibold">{position + 1}</span> {t('of')} {total}
          </p>
          {affectedText}
        </div>
        <button type="button" onClick={onExpand} className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
          <LuPencil size={13} /> {t('change')}
        </button>
      </div>
    );
  }

  const gap = (index) => (
    <li key={`gap-${index}`}>
      <button
        type="button"
        onClick={() => move(index)}
        aria-label={`${t('moveHere')} (${t('position')} ${index + 1})`}
        className="group flex h-6 w-full items-center gap-2 rounded-md px-2 text-[11px] font-medium text-primary/0 transition-colors hover:bg-primary/5 hover:text-primary focus-visible:bg-primary/5 focus-visible:text-primary focus-visible:outline-none"
      >
        <span className="h-px flex-1 border-t border-dashed border-border transition-colors group-hover:border-primary/60 group-focus-visible:border-primary/60" />
        <span>{t('moveHere')}</span>
        <span className="h-px flex-1 border-t border-dashed border-border transition-colors group-hover:border-primary/60 group-focus-visible:border-primary/60" />
      </button>
    </li>
  );

  const items = [];
  for (let index = 0; index <= doors.length; index++) {
    if (index === position) {
      items.push(
        <li key="moved">
          <div
            ref={movedRef}
            tabIndex={0}
            role="option"
            aria-selected="true"
            onKeyDown={(e) => {
              if (e.key === 'ArrowUp') { e.preventDefault(); move(position - 1); }
              if (e.key === 'ArrowDown') { e.preventDefault(); move(position + 1); }
            }}
            className="flex items-center gap-2 rounded-lg bg-primary px-3 py-2 text-primary-foreground shadow-sm transition-all duration-200 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary/25"
          >
            <span className="text-sm font-semibold">{translate('global.door')} {movedDoor?.doorNumber}</span>
            <span className="rounded bg-primary-foreground/20 px-1.5 text-[11px] font-medium">{t('here')}</span>
            <span className="ml-auto text-[11px] opacity-80">P{movedDoor?.doorSequence}</span>
            <span className="flex items-center gap-0.5">
              <button type="button" onClick={() => move(position - 1)} disabled={position === 0} aria-label={t('moveUp')}
                className="rounded p-1 hover:bg-primary-foreground/20 disabled:opacity-40 disabled:hover:bg-transparent">
                <LuChevronUp size={15} />
              </button>
              <button type="button" onClick={() => move(position + 1)} disabled={position === doors.length} aria-label={t('moveDown')}
                className="rounded p-1 hover:bg-primary-foreground/20 disabled:opacity-40 disabled:hover:bg-transparent">
                <LuChevronDown size={15} />
              </button>
            </span>
          </div>
        </li>
      );
    } else {
      items.push(gap(index));
    }
    if (index < doors.length) {
      const door = doors[index];
      items.push(
        <li key={door.id || door.boxitDoorId} className="flex items-center justify-between px-3 py-1 text-sm text-muted-foreground">
          <span>{translate('global.door')} {door.doorNumber}</span>
          <span className="text-[11px]">P{door.doorSequence}</span>
        </li>
      );
    }
  }

  return (
    <div className="col-span-12 mb-3 rounded-xl border border-primary/20 bg-primary/[0.03] p-3 animate-in fade-in-0 slide-in-from-top-1 duration-200">
      <div className="mb-2 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">{t('title')} {columnId}</p>
          <p className="text-xs text-muted-foreground">{t('hint')}</p>
        </div>
        <button type="button" onClick={onCollapse} className="shrink-0 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
          {t('done')}
        </button>
      </div>
      <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">{t('top')}</p>
      <ol role="listbox" aria-label={`${t('title')} ${columnId}`} className="max-h-64 overflow-y-auto py-1">
        {items}
      </ol>
      <p className="px-3 text-[10px] font-semibold uppercase tracking-[0.06em] text-muted-foreground/80">{t('bottom')}</p>
      {affectedText && <div className="mt-2 border-t border-border/60 pt-2">{affectedText}</div>}
    </div>
  );
};

export default DoorPositionPicker;
